/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, useRef } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";

import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import "./i18n"; // Make sure this is imported before any components
import { useTranslation } from "react-i18next";

// Material Dashboard 2 React routes
import getRoutess from "routes";
import getURoutes from "uroutes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

//Authentication
import { useAuth } from "context/authContext";
import ChatFAB from "components/ChatFAB";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import LoadingScreen from "components/Loading";
import i18n from "i18next";
import { setDirection } from "context";
import AppThemeProvider from "context/themeContext";
import { useAppTheme } from "context/themeContext";
import ExternalForm from "layouts/admin/talentAcquisition/ExternalForm";
// import ExternalFormUser from "layouts/user/talentAcquisition/ExternalForm";

export default function App() {
  const {
    isAuthenticated,
    viewAsAdmin,
    employee,
    companies,
    isTopManagement,
    modules,
    companyModules,
  } = useAuth();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor, darkMode } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const [loading, setLoading] = useState(false);
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const allRoutes = useMemo(() => {
    return viewAsAdmin
      ? getRoutess(t, employee?.role, modules) // admin routes
      : getURoutes(t, employee?.role, isTopManagement); // user routes
  }, [viewAsAdmin, t, employee, isTopManagement]);

  const visibleRoutes = useMemo(() => {
    const companyModuleNames = companyModules?.map((m) => m.moduleName) || [];
    if (companyModuleNames?.includes("ALL")) return allRoutes;
    return allRoutes?.filter(
      (route) =>
        !route.module ||
        companyModuleNames.includes(route.module) ||
        route.module === "Organization" ||
        route.module === "Strategic Info"
    );
  }, [allRoutes, companyModules]);
  const company = companies ? companies[0] : {};
  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    if (employee) {
      if (employee.language !== "en") {
        direction != "rtl" ? setDirection(dispatch, "rtl") : null;
      } else {
        direction != "ltr" ? setDirection(dispatch, "ltr") : null;
      }
    }
  }, [employee]);

  // Setting the dir attribute for the body element
  useEffect(() => {
    setLoading(true);
    document.body.setAttribute("dir", direction);
    const changeLanguage = async () => {
      const desiredLang = direction === "ltr" ? "en" : "ar";
      if (i18n.language !== desiredLang) {
        await i18n.changeLanguage(desiredLang);
      }
    };
    changeLanguage().then((e) => {
      setLoading(false);
    });
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <AppThemeProvider
        theme={darkMode ? themeDarkRTL : themeRTL}
        externalPrimaryColor={company?.primaryColor}
        externalNavBarColor={company?.navBarColor}
        externalSideNavColor={company?.sideNavColor}
        externalBackgroundColor={company?.backgroundColor}
        externalNavBarTextColor={company?.textColor}
        externalTopNavBarTextColor={company?.topNavBarTextColor}
      >
        {loading ? (
          <LoadingScreen />
        ) : (
          <>
            <CssBaseline />
            {isAuthenticated && layout === "dashboard" && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brand={companies[0].companyLogo}
                  brandName={companies[0].companyName}
                  employeeName={employee?.name}
                  photo={employee?.photo}
                  routes={visibleRoutes}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
                <Configurator />
              </>
            )}
            {layout === "vr" && isAuthenticated && <Configurator />}
            <Routes>
              <Route path="/forms/:tenantKey/:formId/:employeeId" element={<ExternalForm />} />
              {/* <Route
               path="/formsUser/:tenantKey/:formId/:employeeId"
               element={<ExternalFormUser />}
              /> */}
              {getRoutes(visibleRoutes)}
              {isAuthenticated ? (
                viewAsAdmin ? (
                  // Admin page route
                  <Route path="*" element={<Navigate to="/dashboard" />} />
                ) : (
                  // Non-admin user route
                  <Route path="*" element={<Navigate to="/home" />} />
                )
              ) : (
                <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
              )}
            </Routes>
            {isAuthenticated && !viewAsAdmin && (
              <ChatFAB employeeId={employee?.id} direction={direction} />
            )}
          </>
        )}
      </AppThemeProvider>
    </CacheProvider>
  ) : (
    <AppThemeProvider
      theme={darkMode ? themeDark : theme}
      externalPrimaryColor={company?.primaryColor}
      externalNavBarColor={company?.navBarColor}
      externalSideNavColor={company?.sideNavColor}
      externalBackgroundColor={company?.backgroundColor}
      externalNavBarTextColor={company?.textColor}
      externalTopNavBarTextColor={company?.topNavBarTextColor}
    >
      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          <CssBaseline />
          {isAuthenticated && layout === "dashboard" && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={companies[0].companyLogo}
                brandName={companies[0].companyName}
                employeeName={employee?.name}
                photo={employee?.photo}
                routes={visibleRoutes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              <Configurator />
            </>
          )}
          {layout === "vr" && isAuthenticated && viewAsAdmin && <Configurator />}
          <Routes>
            <Route path="/forms/:tenantKey/:formId/:employeeId" element={<ExternalForm />} />
            {getRoutes(visibleRoutes)}
            {isAuthenticated ? (
              viewAsAdmin ? (
                // Admin page route
                <Route path="*" element={<Navigate to="/dashboard" />} />
              ) : (
                // Non-admin user route
                <Route path="*" element={<Navigate to="/home" />} />
              )
            ) : (
              <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
            )}
          </Routes>
          {isAuthenticated && !viewAsAdmin && <ChatFAB employeeId={employee?.id} />}
        </>
      )}
    </AppThemeProvider>
  );
}
